import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { ref, getDownloadURL, uploadBytes } from 'firebase/storage'
import { firebaseStorage } from '@/firebase/init'
import {
  setImageUrl,
  setLoading,
  setError,
} from '@/redux/slices/imageSessionSlice'
import { ImageSessionState } from '@/types/redux'
import { notify } from '@/utils/index'
import { trackEventMixpanel } from '@/utils/mixpanel'
import { usePathname } from 'next/navigation'
import authSelector from '@/redux/selectors/auth'
import navigationSelector from '@/redux/selectors/navigation'
import {
  createUploadSession,
  handleImageConversion,
} from '@/components/page-partials/quiz/mobile-upload'
import { LESSON_IMAGE_UPLOADED_FILE_UPLOAD } from '@/utils/mixpanel/eventNames'
import { uploadSourceForPath } from '@/utils/mixpanel/events/upload'

const useImageUpload = (listenForPaste = true) => {
  const dispatch = useDispatch()
  const sessionId = useSelector(
    ({ imageSession }: { imageSession: ImageSessionState }) =>
      imageSession.sessionId,
  )
  const pathname = usePathname()
  const { user } = useSelector(authSelector)
  const { selectedLesson } = useSelector(navigationSelector)

  useEffect(() => {
    if (!listenForPaste) return

    const handlePaste = async (event) => {
      const clipboardItems = event.clipboardData.items
      for (const item of clipboardItems) {
        if (item.kind === 'file' || item.type.startsWith('image/')) {
          let file = item.getAsFile()
          if (file) {
            if (
              !file.type &&
              file.name.split('.').pop()?.toLowerCase() === 'heic'
            ) {
              // Set type for HEIC files
              file = new File([file], file.name, { type: 'image/heic' })
            }
            await handleUpload(file)
            break // Exit the loop after processing the first file
          }
        }
      }
    }

    window.addEventListener('paste', handlePaste)
    return () => {
      window.removeEventListener('paste', handlePaste)
    }
  }, [sessionId, listenForPaste])

  const handleUpload = async (file) => {
    if (!sessionId) {
      console.error('Local file upload failed, sessionId undefined ')

      return false
    }

    if (file) {
      const validImageTypes = [
        'image/jpeg',
        'image/png',
        'image/gif',
        'image/heic',
      ]

      if (
        !validImageTypes.includes(file.type) &&
        file.name.split('.').pop().toLowerCase() !== 'heic'
      ) {
        notify('error', 'Invalid file type. Please select an image.')

        return false
      }

      if (file.size > 10 * 1024 * 1024) {
        notify('error', 'File size exceeds 10MB. Please select a smaller file.')

        return false
      }

      dispatch(setError(false))
      dispatch(setLoading(true))

      try {
        await createUploadSession(sessionId)

        const convertedFile = await handleImageConversion(file)
        const storageRef = ref(firebaseStorage, `uploads/${sessionId}.webp`)

        await uploadBytes(storageRef, convertedFile)

        const downloadURL = await getDownloadURL(storageRef)
        dispatch(setImageUrl(downloadURL))
        notify('success', 'Image uploaded successfully.')

        // track Mix Panel Event
        // No event on the demo lesson (home page). This hook also serves
        // Axi and the papers, so the surface goes in `source`.
        if (pathname !== '/') {
          trackEventMixpanel(LESSON_IMAGE_UPLOADED_FILE_UPLOAD, {
            role: user?.role,
            curriculumLevel: user?.curriculumLevel,
            examBoard: user?.examBoard,
            lessonId: selectedLesson?.id,
            source: uploadSourceForPath(pathname),
          })
        }
        return true
      } catch (error) {
        console.error('Image upload failed', error)
        notify('error', 'Image upload failed. Please try again.')
        dispatch(setError(true))
        return false
      } finally {
        dispatch(setLoading(false))
      }
    }
    return false
  }

  return {
    handleUpload,
  }
}

export default useImageUpload
